import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Avatar } from "../../components/Avatar.js";
import { Button, inputStyle, Notice, Selector } from "../../components/ui.js";
import { hasRole, useAuth } from "../../lib/auth.js";
import { addFixture, editFixture, removeFixture } from "../../lib/draw.js";
import type { DrawFixture, EventEntry, FixtureSide, TournamentEvent } from "../../lib/types.js";
import { EventGate, EventScreen, rosterOf, useDraw, type Roster } from "./shared.js";

/**
 * Changing one match in a draw that is already being played.
 *
 * Somebody rolls an ankle in match three of twelve. The organiser does not want a new draw —
 * three results are already in, and a redraw would delete them — they want the four matches
 * that player is still down for to say somebody else's name. That is this screen: one
 * fixture, the people on it, and the way out of it.
 *
 * Its own URL for the same reason scoring has one. Editing a fixture is a thing you arrive at
 * from a specific line in the draw, holding a phone, in a hall, and it has no business sharing
 * a page with Redraw.
 *
 * The same screen adds one, at `/fixture/new`. Picking four people out of the entry list and
 * saying why is the same errand either way, and a second screen for it would be the same
 * dropdowns with a different verb.
 *
 * The role check below is routing, not defence. `edit_fixture`, `remove_fixture` and
 * `add_fixture` refuse anybody who is not an organiser whether or not this page renders.
 */

/** The fixture id that means "one that does not exist yet". */
const NEW = "new";

export function EventFixture() {
  return <EventGate>{({ event }) => <Fixture event={event} />}</EventGate>;
}

function Fixture({ event }: { event: TournamentEvent }) {
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const { member } = useAuth();
  const { data: fixtures = [], isPending } = useDraw(event.id);

  if (!hasRole(member, "organiser")) return <Navigate to={`/events/${event.id}/draw`} replace />;

  const fixture = fixtures.find((entry) => entry.id === fixtureId);

  if (isPending) {
    return (
      <EventScreen event={event} title={fixtureId === NEW ? "Add a match" : "Edit the match"}>
        <p className="micro">Loading…</p>
      </EventScreen>
    );
  }

  if (fixtureId === NEW) return <NewMatch event={event} fixtures={fixtures} />;

  if (!fixture) {
    return (
      <EventScreen event={event} title="Edit the match">
        <Notice tone="attention">That match isn&rsquo;t in the draw any more.</Notice>
      </EventScreen>
    );
  }

  return <Editor event={event} fixture={fixture} />;
}

function Editor({ event, fixture }: { event: TournamentEvent; fixture: DrawFixture }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roster = rosterOf(event);

  const [sideA, setSideA] = useState<string[]>(drawnPlayers(fixture.sideA));
  const [sideB, setSideB] = useState<string[]>(drawnPlayers(fixture.sideB));
  const [reason, setReason] = useState("");
  const [removing, setRemoving] = useState(false);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ["draw", event.id] });
    navigate(`/events/${event.id}/draw`);
  };

  const save = useMutation({
    mutationFn: () => editFixture(fixture.id, sideA, sideB, reason),
    onSuccess: done,
  });
  const drop = useMutation({
    mutationFn: () => removeFixture(fixture.id, reason),
    onSuccess: done,
  });

  const played = Boolean(fixture.result);
  // A knockout slot reading "winner of #3" is a link, not a list of people. Overwriting it
  // with names would disconnect the bracket, so the database refuses it and this says why
  // rather than offering pickers that cannot be saved.
  const derived = fixture.sideA.kind !== "players" || fixture.sideB.kind !== "players";

  const busy = save.isPending || drop.isPending;
  const enough = reason.trim().length >= 3;
  const changed =
    sideA.join() !== drawnPlayers(fixture.sideA).join() ||
    sideB.join() !== drawnPlayers(fixture.sideB).join();

  // Everybody the database will accept: entered, and not withdrawn. A waitlisted player is
  // offered because on the night they are exactly who steps in, and they are marked so the
  // organiser knows they are pulling somebody out of the queue.
  const available = event.entries.filter((entry) => entry.status !== "withdrawn");
  const taken = [...sideA, ...sideB];

  if (played || derived) {
    return (
      <EventScreen event={event} title="Edit the match">
        <Heading fixture={fixture} roster={roster} />
        <Notice tone="attention">
          {played
            ? "This match has been played. Fix the score on it from the results, or void the match if it should not count at all."
            : "This match is filled in by earlier results — whoever wins the match that feeds it plays here. Change that one instead."}
        </Notice>
        <Button onClick={() => navigate(`/events/${event.id}/draw`)}>Back to the draw</Button>
      </EventScreen>
    );
  }

  return (
    <EventScreen event={event} title="Edit the match">
      <Heading fixture={fixture} roster={roster} />

      <SidePicker
        label="This side"
        ids={sideA}
        onChange={setSideA}
        available={available}
        taken={taken}
        roster={roster}
      />
      <SidePicker
        label="Against"
        ids={sideB}
        onChange={setSideB}
        available={available}
        taken={taken}
        roster={roster}
      />

      <label className="mt-6 block">
        <span className="field-label">
          {removing ? "Why it is coming out" : "Why it is changing"}
        </span>
        <input
          value={reason}
          onChange={(input) => setReason(input.target.value)}
          placeholder={removing ? "Nobody to replace Kilo" : "Kilo turned an ankle"}
          maxLength={140}
          style={inputStyle}
        />
        <span className="mt-1.5 block text-sm text-ink-muted">
          Required. It goes in the audit trail with who changed it — somebody else&rsquo;s evening
          is being rearranged.
        </span>
      </label>

      {removing ? (
        <>
          <Notice tone="attention">
            Taking this match out leaves everyone in it a match short, and the numbers of the
            matches after it do not change — #{fixture.sequence} is simply gone.
          </Notice>
          <div className="flex flex-wrap gap-3">
            <Button tone="primary" onClick={() => drop.mutate()} disabled={!enough || busy}>
              {drop.isPending ? "Removing…" : "Take it out of the draw"}
            </Button>
            <Button onClick={() => setRemoving(false)} disabled={busy}>
              Keep it
            </Button>
          </div>
        </>
      ) : (
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            tone="primary"
            onClick={() => save.mutate()}
            disabled={!enough || !changed || busy}
          >
            {save.isPending ? "Saving…" : "Save the change"}
          </Button>
          <Button onClick={() => navigate(`/events/${event.id}/draw`)} disabled={busy}>
            Cancel
          </Button>
        </div>
      )}

      {!removing && !enough && changed ? <p className="micro mt-2">A reason is required.</p> : null}

      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
      {drop.error ? <Notice tone="attention">{drop.error.message}</Notice> : null}

      {!removing ? (
        <button
          type="button"
          onClick={() => setRemoving(true)}
          className="micro mt-8 underline"
          style={{ minHeight: 44 }}
        >
          Take this match out of the draw
        </button>
      ) : null}
    </EventScreen>
  );
}

/**
 * One more match on the end of the draw.
 *
 * The replacement who arrives at eight and is owed a game, the pair who sat out the round
 * that went wrong, the tie that finishes early with the hall still booked. Until this, adding
 * a match meant redrawing, which discards every result already in.
 *
 * **It opens with the people who have played least.** That is not a convenience, it is the
 * answer to the question that made somebody tap Add: a match is being added because a player
 * is a game short, and in an americano the four names at the bottom of the appearance count
 * are nearly always the right four. They are dropdowns, so being wrong about that costs a tap.
 */
function NewMatch({ event, fixtures }: { event: TournamentEvent; fixtures: DrawFixture[] }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roster = rosterOf(event);

  const perSide = event.kind === "singles" ? 1 : 2;
  const available = event.entries.filter((entry) => entry.status !== "withdrawn");
  const owed = leastPlayed(available, fixtures);

  const [sideA, setSideA] = useState<string[]>(owed.slice(0, perSide));
  const [sideB, setSideB] = useState<string[]>(owed.slice(perSide, perSide * 2));
  const [reason, setReason] = useState("");

  const add = useMutation({
    mutationFn: () => addFixture(event.id, sideA, sideB, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["draw", event.id] });
      navigate(`/events/${event.id}/draw`);
    },
  });

  // A bracket is a shape rather than a list: every slot after the first round is fed by an
  // earlier match, so a fixture on the end belongs to nothing. The database refuses it; this
  // says so instead of offering pickers that cannot be saved.
  if (event.format === "knockout") {
    return (
      <EventScreen event={event} title="Add a match">
        <Notice tone="attention">
          A knockout is a bracket — every match after the first round is played by whoever wins an
          earlier one, so there is no end to add to. Redraw it with everybody who is here.
        </Notice>
        <Button onClick={() => navigate(`/events/${event.id}/draw`)}>Back to the draw</Button>
      </EventScreen>
    );
  }

  if (available.length < perSide * 2) {
    return (
      <EventScreen event={event} title="Add a match">
        <Notice tone="attention">
          {perSide * 2} entrants are needed for a match and this event has {available.length}. Add
          somebody to the entries first.
        </Notice>
        <Button onClick={() => navigate(`/events/${event.id}/entries`)}>Who&rsquo;s in</Button>
      </EventScreen>
    );
  }

  const taken = [...sideA, ...sideB];
  const enough = reason.trim().length >= 3;

  return (
    <EventScreen event={event} title="Add a match">
      <p className="micro -mt-4 mb-6">
        Match #{(fixtures[fixtures.length - 1]?.sequence ?? 0) + 1} · on the end of the draw
      </p>

      <SidePicker
        label="This side"
        ids={sideA}
        onChange={setSideA}
        available={available}
        taken={taken}
        roster={roster}
      />
      <SidePicker
        label="Against"
        ids={sideB}
        onChange={setSideB}
        available={available}
        taken={taken}
        roster={roster}
      />

      <label className="mt-6 block">
        <span className="field-label">Why it is being added</span>
        <input
          value={reason}
          onChange={(input) => setReason(input.target.value)}
          placeholder="Alpha and Bravo are a game short"
          maxLength={140}
          style={inputStyle}
        />
        <span className="mt-1.5 block text-sm text-ink-muted">
          Required, and on the record with who added it.
        </span>
      </label>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button tone="primary" onClick={() => add.mutate()} disabled={!enough || add.isPending}>
          {add.isPending ? "Adding…" : "Add the match"}
        </Button>
        <Button onClick={() => navigate(`/events/${event.id}/draw`)} disabled={add.isPending}>
          Cancel
        </Button>
      </div>

      {add.error ? <Notice tone="attention">{add.error.message}</Notice> : null}
    </EventScreen>
  );
}

/**
 * Everybody entered, fewest appearances in the draw first.
 *
 * Counts fixtures rather than results, because a player drawn in four matches who has played
 * one is not owed three more — they are already down for them. Ties break on the name, so the
 * same event opens the same way twice.
 */
function leastPlayed(available: EventEntry[], fixtures: DrawFixture[]): string[] {
  const appearances = new Map<string, number>();
  for (const fixture of fixtures) {
    for (const id of [...(fixture.playersA ?? []), ...(fixture.playersB ?? [])]) {
      appearances.set(id, (appearances.get(id) ?? 0) + 1);
    }
  }

  return [...available]
    .sort(
      (left, right) =>
        (appearances.get(left.memberId) ?? 0) - (appearances.get(right.memberId) ?? 0) ||
        left.displayName.localeCompare(right.displayName),
    )
    .map((entry) => entry.memberId);
}

/** The people drawn on a side, or nobody for a bye and for a slot an earlier round fills in. */
const drawnPlayers = (side: FixtureSide): string[] =>
  side.kind === "players" ? side.memberIds : [];

/** Which match this is, in the words the organiser has been using for it all evening. */
function Heading({ fixture, roster }: { fixture: DrawFixture; roster: Roster }) {
  const names = (ids: string[] | null | undefined) =>
    (ids ?? []).map((id) => roster.get(id)?.name ?? "withdrawn").join(" & ");

  return (
    <p className="micro -mt-4 mb-6">
      Match #{fixture.sequence} · {names(fixture.playersA)} v {names(fixture.playersB)}
    </p>
  );
}

/**
 * One side, a slot at a time.
 *
 * A dropdown per place rather than a typeahead: the candidates are the people entered for
 * this event, which is a dozen or two, and a select is the one control on a phone that
 * everybody already knows how to use. Anybody already on court in this fixture is out of the
 * other lists, so a player cannot be put on both sides of their own match.
 */
function SidePicker({
  label,
  ids,
  onChange,
  available,
  taken,
  roster,
}: {
  label: string;
  ids: string[];
  onChange: (next: string[]) => void;
  available: EventEntry[];
  /** Everyone in this fixture, both sides — nobody is offered twice. */
  taken: string[];
  roster: Roster;
}) {
  return (
    <section className="mb-6">
      <p className="micro mb-2">{label}</p>
      {ids.map((id, index) => {
        const person = roster.get(id);
        const options = available.filter(
          (entry) => entry.memberId === id || !taken.includes(entry.memberId),
        );

        return (
          <div
            key={`${id}-${index}`}
            className="flex items-center justify-between gap-3 border-t py-2"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <span className="flex min-w-0 items-center gap-2">
              <Avatar name={person?.name ?? "withdrawn"} url={person?.url ?? null} size={26} />
              <span className="truncate text-sm">{person?.name ?? "withdrawn"}</span>
            </span>

            <Selector
              label={`Who plays this place in ${label.toLowerCase()}`}
              value={id}
              onChange={(next) =>
                onChange(ids.map((current, at) => (at === index ? next : current)))
              }
              options={options.map((entry) => ({
                value: entry.memberId,
                label:
                  entry.status === "waitlist"
                    ? `${entry.displayName} (waiting)`
                    : entry.displayName,
              }))}
            />
          </div>
        );
      })}
    </section>
  );
}
